import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatBubble from './ChatBubble';
import RegistrationForm from './RegistrationForm';
import { getResponse, formatResponse } from './KnowledgeEngine';
import './Assistant.css';

const Assistant = () => {
  // State
  const [messages, setMessages] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [voicesReady, setVoicesReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const chatContainerRef = useRef(null);
  const utteranceRef = useRef(null);
  const isMountedRef = useRef(true);

  // GIF Paths
  const SPEAKING_GIF = '/assets/poojatalking.gif';
  const LISTENING_GIF = '/assets/poojalistening.gif'; 
  const IDLE_GIF = '/assets/poojalistening.gif';

  // Add message helper - UPDATED to properly handle all message types
  const addMessage = (sender, content) => {
    if (isMountedRef.current) {
      // Handle both string and object messages
      const newMessage = typeof content === 'string' 
        ? { sender, text: content }
        : { sender, ...content, text: content.displayText || content.text || '' };
      
      setMessages(prev => [...prev, newMessage]);
    }
  };

  // Smooth scroll
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const observer = new MutationObserver(() => {
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: 'smooth'
      });
    });

    observer.observe(chatContainer, { childList: true });
    return () => observer.disconnect();
  }, []);

  // Initialize speech synthesis
  useEffect(() => {
    isMountedRef.current = true;
    synthRef.current = window.speechSynthesis;

    const handleVoicesChanged = () => {
      if (synthRef.current.getVoices().length > 0) {
        setVoicesReady(true);
      }
    };

    synthRef.current.addEventListener('voiceschanged', handleVoicesChanged);
    handleVoicesChanged();

    return () => {
      isMountedRef.current = false;
      synthRef.current.removeEventListener('voiceschanged', handleVoicesChanged);
      if (utteranceRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  // Stop speaking function
  const stopSpeaking = () => {
    if (synthRef.current && utteranceRef.current) {
      synthRef.current.cancel();
      setIsAssistantSpeaking(false);
    }
  };

  // Improved speak function
  const speak = useCallback((text, lang = 'en') => {
    if (!voicesReady || !synthRef.current || !isMountedRef.current) {
      console.warn('Speech synthesis not ready');
      return;
    }

    // Cancel any ongoing speech
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    utterance.lang = lang === 'hi' ? 'hi-IN' : 'en-IN';
    utterance.rate = 0.92;
    utterance.pitch = 1.18;

    // Find best available voice
    const voices = synthRef.current.getVoices();
    const preferredVoices = [
      'Microsoft Priya Online (Natural) - English (India)',
      'Google हिन्दी',
      'Veena',
      'Neha',
      'Priya'
    ];

    const indianVoice = voices.find(v => 
      preferredVoices.some(name => v.name.includes(name)) ||
      (v.lang.includes('IN') && (v.name.includes('Female') || v.gender === 'female'))
    );

    if (indianVoice) {
      utterance.voice = indianVoice;
    }

    // Event handlers
    utterance.onstart = () => {
      if (isMountedRef.current) {
        setIsAssistantSpeaking(true);
      }
    };

    utterance.onend = () => {
      if (isMountedRef.current) {
        setIsAssistantSpeaking(false);
        setIsProcessing(false);
        utteranceRef.current = null;
      }
    };

    utterance.onerror = (e) => {
      console.error('Speech error:', e.error);
      if (isMountedRef.current) {
        setIsAssistantSpeaking(false);
        setIsProcessing(false);
        utteranceRef.current = null;
      }
    };

    try {
      synthRef.current.speak(utterance);
    } catch (e) {
      console.error('Failed to speak:', e);
      if (isMountedRef.current) {
        setIsAssistantSpeaking(false);
        setIsProcessing(false);
      }
    }
  }, [voicesReady]);

  // Initialize voice recognition with improved error handling
  useEffect(() => {
    if (!voicesReady) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'en-IN';
      recognitionRef.current.interimResults = false;
      recognitionRef.current.continuous = false;

      recognitionRef.current.onstart = () => {
        if (isMountedRef.current) {
          setIsListening(true);
          addMessage('user', '...listening...');
        }
      };

      recognitionRef.current.onresult = (e) => {
        const transcript = e.results[0][0].transcript.trim();
        if (transcript && isMountedRef.current) {
          setIsProcessing(true);
          handleUserInput(transcript);
        }
        setIsListening(false);
      };

      recognitionRef.current.onerror = (e) => {
        console.error('Recognition error:', e.error);
        if (e.error === 'no-speech') {
          addMessage('assistant', "I didn't hear anything. Please try again.");
        } else if (e.error !== 'aborted') {
          addMessage('assistant', "Sorry, I'm having trouble with voice recognition.");
        }
        setIsListening(false);
        setIsProcessing(false);
      };

      recognitionRef.current.onend = () => {
        if (isMountedRef.current) {
          setIsListening(false);
          setMessages(prev => prev.filter(msg => msg.text !== '...listening...'));
        }
      };
    }

    // Initial greeting after 1 second
    const greetingTimer = setTimeout(() => {
      if (isMountedRef.current && messages.length === 0) {
        const greeting = {
          displayText: "Hello! I'm NAINA, your virtual assistant. How can I help you today?",
          speechText: "Hello! I'm NAINA, your virtual assistant. How can I help you today?"
        };
        addMessage('assistant', greeting);
        speak(greeting.speechText);
      }
    }, 1000);

    return () => {
      clearTimeout(greetingTimer);
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [voicesReady, speak]);

  // Handle user input with proper error handling
  const handleUserInput = async (text) => {
    if (!isMountedRef.current || !text.trim()) return;
    
    try {
      // Add user message - FIXED to properly pass string message
      addMessage('user', text);

      // Get response from knowledge engine
      const lang = detectLanguage(text);
      const rawResponse = getResponse(text, lang);
      const response = formatResponse(rawResponse);
      
      // Add assistant message
      addMessage('assistant', response);
      
      // Speak the response
      if (response.speechText) {
        speak(response.speechText, lang);
      }
    } catch (error) {
      console.error('Error processing user input:', error);
      addMessage('assistant', {
        displayText: "Sorry, I encountered an error processing your request.",
        text: "Sorry, I encountered an error processing your request."
      });
      setIsProcessing(false);
    }
  };

  // Detect language
  const detectLanguage = (text) => {
    return /[\u0900-\u097F]/.test(text) ? 'hi' : 'en';
  };

  // Improved toggle listening function
  const toggleListening = () => {
    if (!recognitionRef.current) {
      addMessage('assistant', "Voice recognition is not available in your browser");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      if (isAssistantSpeaking) {
        stopSpeaking();
      }
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        addMessage('assistant', "Couldn't start voice recognition. Please try again.");
      }
    }
  };

  return (
    <div className="pooja-assistant">
      {/* Header */}
      <div className="assistant-header">
        <h1>NAINA Virtual Assistant</h1>
        <div className={`voice-animation ${isListening ? 'active' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      {/* Main Content */}
      <div className="assistant-container">
        {/* Left Side - Larger Avatar */}
        <div className="avatar-section">
          <div className="avatar-wrapper">
            <img 
              src={
                isAssistantSpeaking ? SPEAKING_GIF :
                isListening ? LISTENING_GIF :
                IDLE_GIF
              }
              alt="NAINA Virtual Assistant"
              className="avatar-gif"
            />
          </div>
        </div>

        {/* Right Side - Compact Chat */}
        <div className="chat-section">
          <div className="chat-container" ref={chatContainerRef}>
            {messages.length === 0 ? (
              <div className="no-messages">No message to display</div>
            ) : (
              messages.map((message, index) => (
                <React.Fragment key={index}>
                  <ChatBubble 
                    sender={message.sender}
                    message={message}
                    className={message.text === '...listening...' ? 'listening' : ''}
                  />
                  {/* Render media only for the current message if it exists */}
                  {message.media && message.sender === 'assistant' && (
                    <div className="media-container">
                      <img src={message.media} alt="Fee Structure" className="fee-image" />
                    </div>
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Bottom Buttons */}
      <div className="button-container">
        <button 
          className={`stop-button ${isAssistantSpeaking ? 'active' : ''}`}
          onClick={stopSpeaking}
          disabled={!isAssistantSpeaking}
        >
          <div className="stop-icon"></div>
        </button>

        <button 
          className={`mic-button ${isListening ? 'listening' : ''}`}
          onClick={toggleListening}
          disabled={isAssistantSpeaking && !isListening}
        >
          <div className="mic-icon">
            <div className="mic-lines"></div>
          </div>
          <div className="pulse-effect"></div>
        </button>
      </div>

      {/* Registration Modal */}
      {showRegistration && (
        <RegistrationForm 
          onComplete={() => {
            setShowRegistration(false);
            const response = {
              displayText: "Thank you for registering with us!",
              speechText: "Thank you for registering with us!"
            };
            addMessage('assistant', response);
            speak(response.speechText);
          }}
          onClose={() => setShowRegistration(false)}
        />
      )}
    </div>
  );
};

export default Assistant;